import { EAutoFillTriggerType } from '@library/dto/checklist/test/tests-checklist-auto-fill-condition.dto';
import {
  answersEqualForAutoFill,
  computeAutoFillStateMap,
  hasAutoFillCondition,
  isAnswerEmpty,
} from '@library/functions/checklist-auto-fill-condition/checklist-auto-fill-condition.evaluator';
import { parseAutoFillValue } from '@library/functions/checklist-auto-fill-condition/checklist-auto-fill-value.functions';
import { resolveTestChecklistEntriesFromAction } from '@library/functions/test-checklist-multiple-entries/test-checklist-multiple-entries.utils';
import { EQpQuestionType } from '@library/models/qp-question.models';
import { QpTestsChecklistElementType } from '@library/models/qp-tests-checklist-element.models';
import { ISaveTestAnswer } from '@one/app/pages/isp/pages/inspection/pages/id/shared/services/store/isp-inspection-id-workflow-store.actions';
import { EWorkflowActionType, IWorkflow, IWorkflowAction } from '@one/app/shared/models/workflow/workflow.models';

interface IChecklistAutoFillUpdate {
  id: string;
  value: ISaveTestAnswer['value'] | null;
  elementType: EQpQuestionType;
  fillValue?: string;
  entryUuid?: string;
}

function collectSiblingQuestions(elements: readonly QpTestsChecklistElementType[]): QpTestsChecklistElementType[] {
  return elements.flatMap((element): QpTestsChecklistElementType[] =>
    element.type === EQpQuestionType.DIV && element.elements ? element.elements : [element]
  );
}

function computeSiblingAutoFillUpdates(
  siblings: readonly QpTestsChecklistElementType[],
  entryCount: number,
  biotechEnabled: boolean
): IChecklistAutoFillUpdate[] {
  const states = computeAutoFillStateMap(siblings, entryCount, biotechEnabled);
  const updates: IChecklistAutoFillUpdate[] = [];

  siblings.forEach((question): void => {
    if (!hasAutoFillCondition(question.autoFillCondition)) {
      return;
    }

    if (question.autoFillCondition.triggerType === EAutoFillTriggerType.ENTRY_COUNT) {
      return;
    }

    const state = states.get(question.id);

    if (!state) {
      return;
    }

    updates.push(buildAutoFillUpdate(question, state.active, state.fillValue));
  });

  return updates;
}

function computeWorkflowEntryCountAutoFillUpdates(workflow: IWorkflow, biotechEnabled: boolean): IChecklistAutoFillUpdate[] {
  if (!biotechEnabled) {
    return [];
  }

  const updates: IChecklistAutoFillUpdate[] = [];

  workflow.steps?.forEach((step): void => {
    step.actions?.forEach((action): void => {
      if (action.type !== EWorkflowActionType.TESTS_CHECKLIST || !action.testsChecklist?.content?.elements) {
        return;
      }

      const entryCount = resolveTestChecklistEntriesFromAction(action).length;
      const siblings = collectSiblingQuestions(action.testsChecklist.content.elements as QpTestsChecklistElementType[]);

      siblings.forEach((question): void => {
        if (
          !hasAutoFillCondition(question.autoFillCondition) ||
          question.autoFillCondition.triggerType !== EAutoFillTriggerType.ENTRY_COUNT
        ) {
          return;
        }

        const state = computeAutoFillStateMap(siblings, entryCount, true).get(question.id);

        if (!state) {
          return;
        }

        updates.push(buildAutoFillUpdate(question, state.active, state.fillValue));
      });
    });
  });

  return updates;
}

function buildAutoFillUpdate(question: QpTestsChecklistElementType, active: boolean, fillValue: string): IChecklistAutoFillUpdate {
  if (!active) {
    return {
      id: question.id,
      value: null,
      elementType: question.type,
    };
  }

  return {
    id: question.id,
    value: parseAutoFillValue(question.type, fillValue),
    elementType: question.type,
    fillValue,
  };
}

function shouldDispatchAutoFillUpdate(question: QpTestsChecklistElementType, update: IChecklistAutoFillUpdate): boolean {
  if (update.value == null) {
    return !isAnswerEmpty(question.type, question.value);
  }

  return !answersEqualForAutoFill(question.type, question.value, update.fillValue ?? '');
}

function findQuestionInWorkflowAction(
  action: IWorkflowAction,
  questionId: string,
  entryUuid?: string
): QpTestsChecklistElementType | undefined {
  const content =
    entryUuid && action.testsChecklist?.entries
      ? action.testsChecklist.entries.find((entry): boolean => entry.entryUuid === entryUuid)?.content
      : action.testsChecklist?.content;

  if (!content?.elements) {
    return undefined;
  }

  return collectSiblingQuestions(content.elements as QpTestsChecklistElementType[]).find((question): boolean => question.id === questionId);
}

export type { IChecklistAutoFillUpdate };

export {
  collectSiblingQuestions,
  computeSiblingAutoFillUpdates,
  computeWorkflowEntryCountAutoFillUpdates,
  findQuestionInWorkflowAction,
  shouldDispatchAutoFillUpdate,
};
